import { useQuery } from '@tanstack/react-query'
import { type FC, useCallback, useMemo } from 'react'

import { SELECT_FILTER_OPTION_VALUE_MAP, type SelectFilterOptionValue } from '@/components/select-filter/constants'
import { SelectFilter } from '@/components/select-filter/select-filter'

import { getApplicantFiltersQuery } from '@/lib/queries/business/v1/applicant/filters/queries'
import { cn } from '@/lib/utils'

import { ApplicantsKeywordFilter } from './applicants-keyword-filter/applicants-keyword-filter'
import { ResetApplicantsFilterButton } from './reset-applicants-filter-button/reset-applicants-filter-button'

export type ApplicantsFilterChangeHandler = ({
  keyword,
  tags,
}: {
  keyword: string
  tags: string[]
  tagFilterOption?: SelectFilterOptionValue
}) => void

export interface ApplicantsFilterProps {
  keyword?: string
  tags?: string[]
  disabled?: boolean
  tagFilterOption?: SelectFilterOptionValue
  onChange?: ApplicantsFilterChangeHandler
  onKeywordChange?: (keyword: string) => void
  onTagsChange?: (tags: string[]) => void
}

export const ApplicantsFilter: FC<ApplicantsFilterProps> = ({
  keyword = '',
  tags = [],
  disabled = false,
  tagFilterOption = SELECT_FILTER_OPTION_VALUE_MAP.or,
  onChange,
}) => {
  const { data } = useQuery(getApplicantFiltersQuery())

  const currentFilterParams = useMemo(
    () => ({
      keyword,
      tags,
      tagFilterOption,
    }),
    [keyword, tags, tagFilterOption]
  )

  const handleChange: ApplicantsFilterChangeHandler = useCallback(
    (params) => {
      onChange?.(params)
    },
    [onChange]
  )

  const handleKeywordChange = useCallback(
    (keyword: string) => {
      handleChange?.({ ...currentFilterParams, keyword })
    },
    [handleChange, currentFilterParams]
  )

  const handleTagsChange = useCallback(
    (tags: string[]) => {
      handleChange?.({ ...currentFilterParams, tags })
    },
    [handleChange, currentFilterParams]
  )

  const handleReset = useCallback(() => {
    handleChange?.({ keyword: '', tags: [], tagFilterOption: undefined })
  }, [handleChange])

  const handleSelectOptionChange = useCallback(
    (option: SelectFilterOptionValue) => {
      handleChange?.({ ...currentFilterParams, tagFilterOption: option })
    },
    [handleChange, currentFilterParams]
  )

  return (
    <div className={cn('flex items-center gap-2')}>
      <ApplicantsKeywordFilter disabled={disabled} keyword={keyword} onChange={handleKeywordChange} />

      <SelectFilter
        disabled={disabled}
        buttonText="Tag"
        items={data?.tags ?? []}
        values={tags}
        showsFilterOption
        filterOption={tagFilterOption}
        onChangeValue={handleTagsChange}
        onChangeFilterOption={handleSelectOptionChange}
      />

      {(!!keyword || tags.length > 0) && <ResetApplicantsFilterButton onClick={handleReset} />}
    </div>
  )
}
